import {
  InputClaim,
  NonInteractiveTechnicalProfile,
  OrchestrationStep,
  PolicyComponents,
  SelfAssertedProfile,
  UserJourney,
  ValidationTP,
} from '../xmlParsing/interfaces';
import constants from '../constants/constants';
import { commonClaims, commonTP } from '../constants/commonTP';
import { init_NonInteractiveTechnicalProfile, init_SelfAssertedProfile } from '../xmlParsing/initInterfaces';
import { cloneDeep } from 'lodash';

export const convertIntoObject = (arr: any[]) =>
  arr.reduce((acc: any, curVal: any) => ({ ...acc, [curVal[0]]: curVal[1] }), {});

export const convertIntoArray = (obj: any) => {
  const temp: any = [];
  for (let key in obj) temp.push([key, obj[key]]);
  return temp;
};
export const mergeObjects = (latestFile: PolicyComponents[], attribute: string) => {
  if (latestFile.length === 0) return {};
  else if (latestFile.length === 1) return latestFile[0];
  const _basePolicy: PolicyComponents = latestFile.shift()!;
  latestFile.forEach((file: PolicyComponents) => {
    // @ts-ignore
    _basePolicy[attribute] = { ..._basePolicy[attribute], ...file[attribute] };
  });
  return _basePolicy;
};

export const mergeJourneys = (latestFile: PolicyComponents[], attribute: 'userJourneys' | 'subJourneys') => {
  if (latestFile.length === 0) return {};
  else if (latestFile.length === 1) return latestFile[0];
  const _basePolicy: PolicyComponents = latestFile.shift()!;
  latestFile.forEach((file: PolicyComponents) => {
    // @ts-ignore
    file[attribute].forEach((uj: UserJourney) => {
      // @ts-ignore
      const ind = _basePolicy[attribute].findIndex((el: UserJourney) => el.id === uj.id);
      // if ind === -1 => Add this userjourney
      // else modify the userjourney
      ind === -1
        ? // @ts-ignore
          _basePolicy[attribute].push(uj)
        : uj['orchestrationSteps'].forEach((el: OrchestrationStep) => {
            // @ts-ignore
            const _stepIndex = _basePolicy[attribute][ind]['orchestrationSteps'].findIndex(
              (step: OrchestrationStep) => step['order'] === el['order'],
            );
            if (_stepIndex != -1) {
              // @ts-ignore
              _basePolicy[attribute][ind]['orchestrationSteps'][_stepIndex] = el;
            } else {
              // @ts-ignore
              _basePolicy[attribute][ind]['orchestrationSteps'].push(el);
            }
            // @ts-ignore
            _basePolicy[attribute][ind]['orchestrationSteps'].sort(
              (a: OrchestrationStep, b: OrchestrationStep) => a['order'] - b['order'],
            );
          });
    });
  });
  return _basePolicy;
};

export const getInludedTechnicalProfile = (baseTP: any, includedTP: any) => {
  // includedTP should never come as undefined.
  if (!baseTP) return baseTP;
  if (!includedTP) return baseTP;
  if (baseTP['protocol'][0].length < 1 && baseTP['protocol'][1].length < 1) {
    baseTP['protocol'] = includedTP['protocol'];
  }
  const _baseMetadata = baseTP['metadata'].length === 0 ? [] : convertIntoObject(baseTP['metadata']);
  const _includedMetadata = includedTP['metadata'].length === 0 ? [] : convertIntoObject(includedTP['metadata']);
  baseTP['metadata'] = convertIntoArray({ ..._includedMetadata, ..._baseMetadata });
  [constants.INPUT_CLAIMS, constants.OUTPUT_CLAIMS, constants.DISPLAY_CLAIMS, constants.PERSISTED_CLAIMS].forEach(
    (type: string) => {
      if (baseTP && baseTP[type] && includedTP && includedTP[type]) {
        const baseList: string[] = [];
        baseTP[type].forEach((el: any) => {
          baseList.push(el['claimTypeReferenceId']);
        });
        const _includedList = includedTP[type].reduce((acc: any[], el: any) => {
          if (!baseList.includes(el['claimTypeReferenceId'])) acc = acc.concat(el);
          return acc;
        }, []);
        baseTP[type] = [...baseTP[type], ..._includedList];
      }
    },
  );
  [constants.INPUT_CLAIMS_TRANSFORMATIONS, constants.OUTPUT_CLAIMS_TRANSFORMATIONS].forEach((type: string) => {
    if (baseTP && baseTP[type] && includedTP && includedTP[type]) {
      const _includedList = includedTP[type].reduce((acc: string[], el: string) => {
        if (!baseTP[type].includes(el)) acc = acc.concat(el);
        return acc;
      }, []);
      baseTP[type] = [...baseTP[type], ..._includedList];
    }
  });
  return baseTP;
};

const includeProfilesForAttr = (policy: PolicyComponents, targetAttribute: string, otherAttribute: string) => {
  // @ts-ignore
  Object.keys(policy[targetAttribute]).forEach((refId: string) => {
    // @ts-ignore
    let includedProfile = policy[targetAttribute][refId]['includeTechnicalProfile'];
    while (includedProfile) {
      let includedProfileAttribute = null;
      // @ts-ignore
      if (policy[targetAttribute].hasOwnProperty(includedProfile)) {
        includedProfileAttribute = targetAttribute;
        // @ts-ignore
      } else if (policy[otherAttribute].hasOwnProperty(includedProfile)) {
        includedProfileAttribute = otherAttribute;
      }
      if (!includedProfileAttribute) {
        // @ts-ignore
        policy['claims'] = { ...commonClaims[includedProfile], ...policy['claims'] };
        // @ts-ignore
        // NOTE: Assumes that no commonTPs are self-asserted! If this fact is changed this will break.
        policy[constants.PROTOCOL_NAME.otherProfile][includedProfile] = commonTP[includedProfile];
        includedProfileAttribute = constants.PROTOCOL_NAME.otherProfile;
      }
      // @ts-ignore
      policy[targetAttribute][refId] = getInludedTechnicalProfile(
        // @ts-ignore
        policy[targetAttribute][refId],
        // @ts-ignore
        policy[includedProfileAttribute][includedProfile],
      );
      // @ts-ignore
      includedProfile = policy[includedProfileAttribute][includedProfile]['includeTechnicalProfile'];
    }
  });
};

export const mergeTechnicalProfile = (latestFile: PolicyComponents[], attribute: string) => {
  if (latestFile.length === 0) return {};
  const otherAttribute = attribute === 'selfAssertedProfiles' ? 'otherProfiles' : 'selfAssertedProfiles';
  let _basePolicy: PolicyComponents = latestFile.shift()!;
  // @ts-ignore
  latestFile.forEach((file: PolicyComponents) => {
    // @ts-ignore
    if (Object.keys(file[attribute]).some((key: string) => key in Object.keys(file[otherAttribute]))) {
      // Error case: If multiple TP elements have the same ID in the same file, the XML is invalid.
      console.log('Warning - policy contains multiple TPs with same ID in same file (invalid XML)');
    }
    // @ts-ignore
    let fileRefs = { ...file[attribute], ...file[otherAttribute] };
    // @ts-ignore
    Object.keys(fileRefs).forEach((refId: string) => {
      let targetAttribute: string | null = null;
      // @ts-ignore
      if (_basePolicy[attribute].hasOwnProperty(refId)) {
        targetAttribute = attribute;
      } else if (_basePolicy[otherAttribute].hasOwnProperty(refId)) {
        targetAttribute = otherAttribute;
      }
      if (targetAttribute) {
        // If the refId is already defined under the queried attribute, merge in the current refId regardless of whether it has the same attribute in the current file.
        // update the self asserted profile's metadata
        // @ts-ignore
        const _baseM = convertIntoObject(_basePolicy[targetAttribute][refId][constants.VALIDATION_METADATA]);
        const _parsedM = convertIntoObject(
          // @ts-ignore
          fileRefs[refId][constants.VALIDATION_METADATA],
        );
        Object.keys(_parsedM).forEach((key: string) => {
          _baseM[key] = _parsedM[key];
        });
        const _finalM = convertIntoArray(_baseM);
        // @ts-ignore
        _basePolicy[targetAttribute][refId][constants.VALIDATION_METADATA] = _finalM;
        // update the self asserted profile's input claims
        [constants.INPUT_CLAIMS, constants.OUTPUT_CLAIMS, constants.DISPLAY_CLAIMS, constants.PERSISTED_CLAIMS].forEach(
          (type: string) => {
            // @ts-ignore
            let _base = _basePolicy[targetAttribute][refId][type];
            // @ts-ignore
            const _parsed = fileRefs[refId][type];
            // The current file may have different claimsets defined because refId may be marked with a different attribute
            if (_parsed) {
              if (!_base) {
                // @ts-ignore
                _basePolicy[targetAttribute][refId][type] = [];
                // @ts-ignore
                _base = _basePolicy[targetAttribute][refId][type];
              }
              _parsed.forEach((pel: any, index: number) => {
                const ind = _base.findIndex((bel: any) => pel.claimTypeReferenceId === bel.claimTypeReferenceId);
                ind === -1 ? _base.push(pel) : (_base[ind] = _parsed[index]);
              });
            }
            // @ts-ignore
            _basePolicy[targetAttribute][refId][type] = _base;
          },
        );

        // update the self asserted profile's claimtransformation
        [constants.INPUT_CLAIMS_TRANSFORMATIONS, constants.OUTPUT_CLAIMS_TRANSFORMATIONS].forEach((type: string) => {
          // @ts-ignore
          if (_basePolicy[targetAttribute][refId][type]) {
            // @ts-ignore
            let _base = _basePolicy[targetAttribute][refId][type];
            // @ts-ignore
            const _parsed = fileRefs[refId][type];
            if (_parsed) {
              _parsed.forEach((ct: string) => {
                _basePolicy['claimTransformations'][ct] = file['claimTransformations'][ct];
                if (!_base.includes(ct)) {
                  _base = _base.concat(ct);
                }
              });
            }
            // @ts-ignore
            _basePolicy[targetAttribute][refId][type] = _base;
          }
        });

        // update the self asserted profile's validation technical profiles
        // Note that a profile that is not self-asserted now isn't guaranteed to stay that way,
        // so merge any VTPs defined in this file even if the base profile isn't.
        // @ts-ignore
        let _baseVT = _basePolicy[targetAttribute][refId][constants.VALIDATION_TECHNICAL_PROFILES];
        // @ts-ignore
        const _parsedVT = fileRefs[refId][constants.VALIDATION_TECHNICAL_PROFILES];
        if (_parsedVT) {
          if (!_baseVT) {
            // @ts-ignore
            _basePolicy[targetAttribute][refId][constants.VALIDATION_TECHNICAL_PROFILES] = [];
            // @ts-ignore
            _baseVT = _basePolicy[targetAttribute][refId][constants.VALIDATION_TECHNICAL_PROFILES];
          }
          _parsedVT.forEach((vtp: ValidationTP, index: number) => {
            const ind = _baseVT.findIndex((bel: any) => vtp['id'] === bel['id']);
            ind === -1 ? _baseVT.push(vtp) : (_baseVT[ind] = _parsedVT[index]);
          });
        }

        // Protocols are allowed to be overridden. This can be especially important to merging because a profile can end up self-asserted or non-self-asserted even if it isn't that way in the base file.
        if (
          fileRefs[refId].protocol &&
          (fileRefs[refId]['protocol'][0].length > 0 || fileRefs[refId]['protocol'][1].length > 0)
        ) {
          // @ts-ignore
          _basePolicy[targetAttribute][refId]['protocol'] = fileRefs[refId]['protocol'];
        }
        // @ts-ignore
        if (fileRefs[refId]['includeTechnicalProfile']) {
          // @ts-ignore
          _basePolicy[targetAttribute][refId]['includeTechnicalProfile'] = fileRefs[refId]['includeTechnicalProfile'];
        }
      } else {
        // @ts-ignore
        if (file[attribute].hasOwnProperty(refId)) {
          // @ts-ignore
          _basePolicy[attribute][refId] = fileRefs[refId];
        } else {
          _basePolicy[otherAttribute][refId] = fileRefs[refId];
        }
      }
    });
  });
  // Handle includeTechnicalProfiles
  includeProfilesForAttr(_basePolicy, attribute, otherAttribute);
  includeProfilesForAttr(_basePolicy, otherAttribute, attribute);
  // Including profiles could change whether profiles are self-asserted or not.
  // @ts-ignore
  let swapToOther: string[] = [];
  let swapToSelfAsserted: string[] = [];
  // @ts-ignore
  Object.keys(_basePolicy[constants.PROTOCOL_NAME.selfAssertedProfiles]).forEach((refId: string) => {
    // @ts-ignore
    const protocol = _basePolicy[constants.PROTOCOL_NAME.selfAssertedProfiles][refId].protocol;
    if (protocol) {
      if (!protocol[1].includes('.SelfAssertedAttributeProvider')) {
        swapToOther.push(refId);
      }
    }
  });
  // @ts-ignore
  Object.keys(_basePolicy[constants.PROTOCOL_NAME.otherProfile]).forEach((refId: string) => {
    // @ts-ignore
    const protocol = _basePolicy[constants.PROTOCOL_NAME.otherProfile][refId].protocol;
    if (protocol) {
      if (protocol[1].includes('.SelfAssertedAttributeProvider')) {
        swapToSelfAsserted.push(refId);
      }
    }
  });
  swapToOther.forEach((refId: string) => {
    // @ts-ignore
    _basePolicy[constants.PROTOCOL_NAME.otherProfile][refId] =
      // @ts-ignore
      _basePolicy[constants.PROTOCOL_NAME.selfAssertedProfiles][refId];
    // @ts-ignore
    delete _basePolicy[constants.PROTOCOL_NAME.selfAssertedProfiles][refId];
    // Note that if the base policy has self-asserted-profile-only components (display claims, validation TPs, etc) these won't be displayed
    // but will still exist; they are schema-invalid in AD B2C.
  });
  swapToSelfAsserted.forEach((refId: string) => {
    // @ts-ignore
    _basePolicy[constants.PROTOCOL_NAME.selfAssertedProfiles][refId] =
      // @ts-ignore
      _basePolicy[constants.PROTOCOL_NAME.otherProfile][refId];
    // @ts-ignore
    delete _basePolicy[constants.PROTOCOL_NAME.otherProfile][refId];
  });
  return _basePolicy;
};

const mergeOutputAndDisplayClaims = (
  TPToUpdate: SelfAssertedProfile | NonInteractiveTechnicalProfile,
  refTP: any,
  mergedClaims: any,
  type: 'outputClaims' | 'displayClaims',
) => {
  const _mergedData: any = {};
  const _refTPIds = (refTP[type] || []).reduce((acc: string[], curVal: any) => {
    if (
      mergedClaims.hasOwnProperty(curVal['claimTypeReferenceId']) &&
      mergedClaims[curVal['claimTypeReferenceId']]['userInputType']
    ) {
      _mergedData[curVal['claimTypeReferenceId']] = curVal;
      return acc.concat(curVal['claimTypeReferenceId']);
    }
    return acc;
  }, []);

  // @ts-ignore
  const _existingIds = TPToUpdate[type].reduce((acc: string[], curVal: any) => {
    _mergedData[curVal['claimTypeReferenceId']] = curVal;
    return acc.concat(curVal['claimTypeReferenceId']);
  }, []);

  const _mergedClaims: string[] = _existingIds.reduce((acc: string[], id: string) => {
    return acc.includes(id) ? acc : acc.concat(id);
  }, _refTPIds);

  return _mergedClaims.reduce((acc: any[], curVal: string) => acc.concat(_mergedData[curVal]), []);
};
export const getUpdatedTPData = (
  TPToUpdate: SelfAssertedProfile | NonInteractiveTechnicalProfile,
  refTP: any,
  mergedClaims: any,
  payload: any,
) => {
  if (!TPToUpdate) {
    TPToUpdate =
      payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles
        ? {
            ...init_SelfAssertedProfile(),
            id: refTP['id'],
            displayName: refTP['displayName'],
            claimProvider: refTP['claimProvider'],
            protocol: refTP['protocol'],
          }
        : {
            ...init_NonInteractiveTechnicalProfile(),
            id: refTP['id'],
            displayName: refTP['displayName'],
            claimProvider: refTP['claimProvider'],
            protocol: refTP['protocol'],
          };
  }
  if ([constants.VALIDATION_METADATA].includes(payload['type'])) {
    // @ts-ignore
    TPToUpdate[payload['type']] = payload['data'];
  } else if ([constants.INPUT_CLAIMS, constants.PERSISTED_CLAIMS].includes(payload['type'])) {
    // @ts-ignore
    const _ind = TPToUpdate[payload['type']].findIndex(
      (el: InputClaim) => el['claimTypeReferenceId'] == payload['data']['ioclaim']['claimTypeReferenceId'],
    );
    _ind === -1
      ? // @ts-ignore
        TPToUpdate[payload['type']].push(payload['data']['ioclaim'])
      : // @ts-ignore
        (TPToUpdate[payload['type']][_ind] = payload['data']['ioclaim']);
  } else if ([constants.OUTPUT_CLAIMS, constants.DISPLAY_CLAIMS].includes(payload['type'])) {
    if (payload['protocolName'] === constants.PROTOCOL_NAME.selfAssertedProfiles) {
      // for selfAsserted profiles,
      // copy the display and output claims from the parent files to the modifying file
      // @ts-ignore
      TPToUpdate[payload['type']] = mergeOutputAndDisplayClaims(TPToUpdate, refTP, mergedClaims, payload['type']);
    }
    if (
      payload['type'] === constants.DISPLAY_CLAIMS &&
      // @ts-ignore
      TPToUpdate[constants.DISPLAY_CLAIMS].length === 0
    ) {
      const _outputClaims = mergeOutputAndDisplayClaims(TPToUpdate, refTP, mergedClaims, 'outputClaims');
      if (_outputClaims.length > 0) {
        window.alert(
          `The Technical Profile you're currently editing doesn't have any display claims.\nThe output claims with a valid UserInputType will automatically be copied to the display claims.`,
        );
        // @ts-ignore
        TPToUpdate[constants.DISPLAY_CLAIMS] = _outputClaims;
      }
    }
    // @ts-ignore
    const _ind = TPToUpdate[payload['type']].findIndex(
      (el: InputClaim) => el['claimTypeReferenceId'] == payload['data']['ioclaim']['claimTypeReferenceId'],
    );
    _ind === -1
      ? // @ts-ignore
        TPToUpdate[payload['type']].push(payload['data']['ioclaim'])
      : // @ts-ignore
        (TPToUpdate[payload['type']][_ind] = payload['data']['ioclaim']);
  } else if (
    [constants.INPUT_CLAIMS_TRANSFORMATIONS, constants.OUTPUT_CLAIMS_TRANSFORMATIONS].includes(payload['type']) &&
    payload['mode'] === 'add'
  ) {
    // @ts-ignore
    TPToUpdate[payload['type']].push(payload['data']['id']);
  } else if ([constants.VALIDATION_TECHNICAL_PROFILES].includes(payload['type'])) {
    // @ts-ignore
    const _ind = TPToUpdate[payload['type']].findIndex((el: any) => el['id'] == payload['data']['id']);
    const updateData = cloneDeep(payload['data']);
    delete updateData['vtpType'];
    _ind === -1
      ? // @ts-ignore
        TPToUpdate[payload['type']].push(updateData)
      : // @ts-ignore
        (TPToUpdate[payload['type']][_ind] = updateData);
  }
  return TPToUpdate;
};
